import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';

import { useAppSelector } from '../../hooks';
import { TAccountClientSourceList, TAccountClientSourceListResponse, TAccountClientSourceQuery, adaptAccountClientSourceFromListResponse, adaptAccountClientSourceQueryToRequest } from '../../types';
import { BACKEND_SOURCE_ACCOUNT_CLIENT } from '../../backend-paths';


export const sourceAccountClientGetApi = createAsyncThunk(
	BACKEND_SOURCE_ACCOUNT_CLIENT,
	(payload: TAccountClientSourceQuery | undefined, thunkApi) => {
		const params = adaptAccountClientSourceQueryToRequest(payload);

		return HttpService.get<TAccountClientSourceListResponse>(BACKEND_SOURCE_ACCOUNT_CLIENT, { params }).then(result => {
			return adaptAccountClientSourceFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TAccountClientSourceList;
	query: TAccountClientSourceQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 50,
		offset: 0,
		search: undefined,
	},
	error: undefined,
};

export const sourceAccountClientSlice = createSlice({
	name: 'sourceAccountClientSlice',
	initialState,
	reducers: {
		setSourceAccountClientQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		clearSourceAccountClient(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[sourceAccountClientGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[sourceAccountClientGetApi.fulfilled.type]: (state, action: PayloadAction<TAccountClientSourceList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[sourceAccountClientGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useSourceAccountClientSelector = () => useAppSelector(state => state[sourceAccountClientSlice.name]);

export const { clearSourceAccountClient, setSourceAccountClientQuerySearch } = sourceAccountClientSlice.actions;
